import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileCopy as CopyIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNotification } from '../../contexts/NotificationContext';
import PageHeader from '../../components/common/PageHeader';
import DeleteConfirmDialog from '../../components/common/DeleteConfirmDialog';
import { useAuth } from '../../hooks/useAuth';
import plantillaService from '../../services/plantillaService';

// Componente principal
const PlantillasMensajes = () => {
  const { showNotification } = useNotification();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [plantillas, setPlantillas] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPlantilla, setSelectedPlantilla] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testDestination, setTestDestination] = useState('');

  const isAdmin = user?.rol === 'Administrador';
  const tiposPlantilla = ['WhatsApp', 'Email', 'SMS'];

  // Cargar plantillas
  useEffect(() => {
    const fetchPlantillas = async () => {
      setLoading(true);
      try {
        const response = await plantillaService.getPlantillas();
        setPlantillas(response.data || []);
      } catch (error) {
        console.error('Error fetching message templates:', error);
        showNotification('Error al cargar las plantillas de mensajes', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchPlantillas();
  }, [refreshKey, showNotification]);

  // Agrupar plantillas por tipo
const groupedPlantillas = tiposPlantilla.reduce((acc, tipo) => {
  acc[tipo] = plantillas.filter(p => p.tipo === tipo);
  return acc;
}, {});

  // Esquema de validación con Yup
  const validationSchema = Yup.object({
  nombre: Yup.string()
    .required('El nombre es requerido')
    .max(100, 'El nombre no puede exceder los 100 caracteres'),
  tipo: Yup.string()
    .required('El tipo es requerido')
    .oneOf(tiposPlantilla, 'Tipo de plantilla no válido'),
  ocasion: Yup.string()
    .max(100, 'La ocasión no puede exceder los 100 caracteres'),
  asunto: Yup.string()
    .max(255, 'El asunto no puede exceder los 255 caracteres'),
  contenido: Yup.string()
    .required('El contenido es requerido')
});

  // Formik para manejar el formulario
  const formik = useFormik({
    initialValues: {
      nombre: '',
      tipo: 'WhatsApp',
      ocasion: '',
      asunto: '',
      contenido: '',
      variables: '{}',
      activo: true
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        // Parsear variables si es JSON
        let parsedVariables = {};
        if (values.tipo === 'Email' && !values.asunto) {
          formik.setFieldError('asunto', 'El asunto es requerido para emails');
          return;
        }
        try {
          if (values.variables && typeof values.variables === 'string') {
            parsedVariables = JSON.parse(values.variables);
          } else {
            parsedVariables = values.variables;
          }
        } catch (e) {
          parsedVariables = {};
        }

        const plantillaData = {
          ...values,
          variables: parsedVariables
        };

        if (editMode) {
          await plantillaService.updatePlantilla(selectedPlantilla.id, plantillaData);
          showNotification('Plantilla actualizada con éxito', 'success');
        } else {
          await plantillaService.createPlantilla(plantillaData);
          showNotification('Plantilla creada con éxito', 'success');
        }

        setDialogOpen(false);
        setRefreshKey(prev => prev + 1);
      } catch (error) {
        console.error('Error saving template:', error);
        showNotification(
          `Error al ${editMode ? 'actualizar' : 'crear'} la plantilla`,
          'error'
        );
      }
    }
  });

  // Manejar cambio de pestaña
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Abrir diálogo de creación
  const handleCreateClick = () => {
    setEditMode(false);
    formik.resetForm();
    setDialogOpen(true);
  };

  // Abrir diálogo de edición
  const handleEditClick = (plantilla) => {
    setEditMode(true);
    setSelectedPlantilla(plantilla);

    // Convertir variables de objeto a string si es necesario
    let variablesStr = '{}';
    if (plantilla.variables) {
      if (typeof plantilla.variables === 'string') {
        variablesStr = plantilla.variables;
      } else {
        try {
          variablesStr = JSON.stringify(plantilla.variables, null, 2);
        } catch (e) {
          variablesStr = '{}';
        }
      }
    }

    formik.setValues({
      nombre: plantilla.nombre,
      tipo: plantilla.tipo,
      ocasion: plantilla.ocasion || '',
      asunto: plantilla.asunto || '',
      contenido: plantilla.contenido,
      variables: variablesStr,
      activo: plantilla.activo
    });

    setDialogOpen(true);
  };

  // Abrir diálogo de confirmación para eliminar
  const handleDeleteClick = (plantilla) => {
    setSelectedPlantilla(plantilla);
    setDeleteDialogOpen(true);
  };

  // Confirmar eliminación
  const handleDeleteConfirm = async () => {
    try {
      await plantillaService.deletePlantilla(selectedPlantilla.id);
      showNotification('Plantilla eliminada correctamente', 'success');
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Error deleting template:', error);
      showNotification('Error al eliminar la plantilla', 'error');
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  // Abrir diálogo para probar envío
  const handleTestClick = (plantilla) => {
    setSelectedPlantilla(plantilla);
    setTestDestination('');
    setTestDialogOpen(true);
  };

  // Enviar prueba
  const handleSendTest = async () => {
    try {
      await plantillaService.testPlantilla(selectedPlantilla.id, { destinatario: testDestination });
      showNotification('Prueba enviada correctamente', 'success');
      setTestDialogOpen(false);
    } catch (error) {
      console.error('Error sending test:', error);
      showNotification('Error al enviar la prueba', 'error');
    }
  };

  // Duplicar plantilla
  const handleDuplicateClick = async (plantilla) => {
    try {
      // Crear copia con nombre modificado
      const newName = `Copia de ${plantilla.nombre}`;
      
      // Convertir variables a objeto si es necesario
      let variables = plantilla.variables;
      if (typeof variables === 'string') {
        try {
          variables = JSON.parse(variables);
        } catch (e) {
          variables = {};
        }
      }
      
      const newPlantilla = {
        nombre: newName,
        tipo: plantilla.tipo,
        ocasion: plantilla.ocasion,
        asunto: plantilla.asunto,
        contenido: plantilla.contenido,
        variables,
        activo: true
      };
      
      await plantillaService.createPlantilla(newPlantilla);
      showNotification('Plantilla duplicada con éxito', 'success');
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Error duplicating template:', error);
      showNotification('Error al duplicar la plantilla', 'error');
    }
  };

  // Renderizar tabla de plantillas
  const renderPlantillasTable = (tipo) => {
    const plantillasFiltradas = groupedPlantillas[tipo] || [];
    
    if (plantillasFiltradas.length === 0) {
      return (
        <Box p={3} textAlign="center">
          <Typography variant="body1" color="textSecondary">
            No hay plantillas de {tipo} registradas.
          </Typography>
          {isAdmin && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleCreateClick}
              sx={{ mt: 2 }}
            >
              Crear Nueva Plantilla
            </Button>
          )}
        </Box>
      );
    }
    
    return (
      <TableContainer component={Paper} elevation={0}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Ocasión</TableCell>
              {tipo === 'Email' && <TableCell>Asunto</TableCell>}
              <TableCell>Estado</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {plantillasFiltradas.map((plantilla) => (
              <TableRow key={plantilla.id}>
                <TableCell>{plantilla.nombre}</TableCell>
                <TableCell>{plantilla.ocasion || '-'}</TableCell>
                {tipo === 'Email' && <TableCell>{plantilla.asunto || '-'}</TableCell>}
                <TableCell>
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{
                      color: plantilla.activo ? 'success.main' : 'text.disabled',
                      fontWeight: 'medium'
                    }}
                  >
                    {plantilla.activo ? 'Activa' : 'Inactiva'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Tooltip title="Probar">
                      <IconButton
                        size="small"
                        color="info"
                        onClick={() => handleTestClick(plantilla)}
                        sx={{ mr: 1 }}
                      >
                        <SendIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Duplicar">
                      <IconButton
                        size="small"
                        color="secondary"
                        onClick={() => handleDuplicateClick(plantilla)}
                        sx={{ mr: 1 }}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {isAdmin && (
                      <>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditClick(plantilla)}
                            sx={{ mr: 1 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteClick(plantilla)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <PageHeader
        title="Plantillas de Mensajes"
        breadcrumbs={[
          { text: 'Dashboard', to: '/dashboard' },
          { text: 'Configuración', to: '/configuracion' },
          { text: 'Plantillas de Mensajes' }
        ]}
        actionButton={
          isAdmin && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleCreateClick}
            >
              Nueva Plantilla
            </Button>
          )
        }
      />

      <Card>
        <CardContent>
          {loading ? (
            <Box display="flex" justifyContent="center" my={5}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  variant="fullWidth"
                >
                  {tiposPlantilla.map((tipo, index) => (
                    <Tab key={index} label={tipo} />
                  ))}
                </Tabs>
              </Box>
              
              {tiposPlantilla.map((tipo, index) => (
                <Box key={index} hidden={tabValue !== index} sx={{ py: 2 }}>
                  {tabValue === index && renderPlantillasTable(tipo)}
                </Box>
              ))}
            </>
          )}
        </CardContent>
      </Card>

      {/* Diálogo para crear/editar plantilla */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle>
            {editMode ? 'Editar Plantilla' : 'Nueva Plantilla'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="nombre"
                  name="nombre"
                  label="Nombre *"
                  value={formik.values.nombre}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.nombre && Boolean(formik.errors.nombre)}
                  helperText={formik.touched.nombre && formik.errors.nombre}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="tipo-label">Tipo *</InputLabel>
                  <Select
                    labelId="tipo-label"
                    id="tipo"
                    name="tipo"
                    value={formik.values.tipo}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.tipo && Boolean(formik.errors.tipo)}
                    label="Tipo *"
                  >
                    {tiposPlantilla.map((tipo) => (
                      <MenuItem key={tipo} value={tipo}>{tipo}</MenuItem>
                    ))}
                  </Select>
                  {formik.touched.tipo && formik.errors.tipo && (
                    <FormHelperText error>{formik.errors.tipo}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="ocasion"
                  name="ocasion"
                  label="Ocasión"
                  value={formik.values.ocasion}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.ocasion && Boolean(formik.errors.ocasion)}
                  helperText={formik.touched.ocasion && formik.errors.ocasion}
                />
              </Grid>
              {formik.values.tipo === 'Email' && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="asunto"
                    name="asunto"
                    label="Asunto *"
                    value={formik.values.asunto}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.asunto && Boolean(formik.errors.asunto)}
                    helperText={formik.touched.asunto && formik.errors.asunto}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="contenido"
                  name="contenido"
                  label="Contenido *"
                  value={formik.values.contenido}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.contenido && Boolean(formik.errors.contenido)}
                  helperText={formik.touched.contenido && formik.errors.contenido}
                  multiline
                  rows={8}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                  Variables disponibles
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Utilice estas variables en el contenido:
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1} my={1}>
                  <Chip label="{{nombre_paciente}}" />
                  <Chip label="{{fecha_cita}}" />
                  <Chip label="{{hora_cita}}" />
                  <Chip label="{{nombre_especialista}}" />
                  <Chip label="{{especialidad}}" />
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)} color="secondary">
              Cancelar
            </Button>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={formik.isSubmitting}
            >
              {formik.isSubmitting ? (
                <CircularProgress size={24} />
              ) : editMode ? (
                'Actualizar'
              ) : (
                'Crear'
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Diálogo para probar envío */}
      <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)}>
        <DialogTitle>Probar Plantilla</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            Enviar prueba de la plantilla "{selectedPlantilla?.nombre}"
          </Typography>
          <TextField
            fullWidth
            label={selectedPlantilla?.tipo === 'Email' ? 'Email de destino' : 'Número de teléfono'}
            value={testDestination}
            onChange={(e) => setTestDestination(e.target.value)}
            margin="normal"
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogOpen(false)} color="secondary">
            Cancelar
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleSendTest}
            disabled={!testDestination}
          >
            Enviar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de confirmación para eliminar */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Eliminar Plantilla"
        content={`¿Estás seguro de que deseas eliminar la plantilla "${selectedPlantilla?.nombre}"? Esta acción no se puede deshacer.`}
      />
    </Box>
  );
};

// Componente Chip para mostrar variables disponibles
const Chip = ({ label }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-block',
      backgroundColor: 'action.selected',
      color: 'text.primary',
      px: 1,
      py: 0.5,
      borderRadius: 1,
      fontSize: '0.75rem',
      fontFamily: 'monospace'
    }}
  >
    {label}
  </Box>
);

export default PlantillasMensajes;